import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/lib/utils';

export interface OverlayDrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  closeOnBackdrop?: boolean;
  ariaLabel?: string;
  className?: string;
  width?: string;
}

/**
 * Right-side drawer identical in look/animation to the shared `@/shared/ui/drawer` Drawer (reuses the same
 * `.phx-drawer*` classes) but pinned to the Phoenix overlay z-index tier (`--phx-z-overlay`) instead of the
 * shared Drawer's fixed `z-9999`. The desktop's per-window z-index grows unboundedly (`10 + stackZ*100`), so
 * a deeply-stacked window can climb above `9999` and cover the shared Drawer; the Cost Scheme board can live
 * several layers deep, so its detail drawer must sit on the overlay tier to stay on top. We don't touch the
 * shared Drawer's global z-index because other consumers depend on it.
 */
export function OverlayDrawer({
  open,
  onClose,
  children,
  closeOnBackdrop = true,
  ariaLabel,
  className,
  width = '560px',
}: OverlayDrawerProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [rendered, setRendered] = useState(open);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      const raf = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (shown && surfaceRef.current) {
      surfaceRef.current.focus();
    }
  }, [shown]);

  if (!rendered || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      style={{ zIndex: 'var(--phx-z-overlay)' }}
      className="phx-drawer-root fixed inset-0 flex justify-end"
    >
      <div
        aria-hidden
        onClick={closeOnBackdrop ? onClose : undefined}
        data-open={shown}
        className="phx-drawer-backdrop bg-phx-backdrop absolute inset-0 backdrop-blur-[1px]"
      />
      <div
        ref={surfaceRef}
        tabIndex={-1}
        data-open={shown}
        onTransitionEnd={(event) => {
          if (!open && event.target === event.currentTarget && event.propertyName === 'transform') {
            setRendered(false);
          }
        }}
        style={{ width: `clamp(400px, ${width}, 66.67vw)` }}
        className={cn(
          'phx-drawer bg-phx-surface text-phx-text border-phx-border shadow-phx-lg relative z-10 flex h-full max-w-full flex-col border-l outline-none',
          className
        )}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
